import { CircularProgress, TextField } from '@material-ui/core';
import {
  Add,
  Cancel,
  CheckCircle,
  DragHandle as Equal,
  Edit,
} from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BOOKING_STAGES } from '../../../constants';

import { setQuestionLevels } from '../../../redux/reducers/questionLevelsReducer';
import {
  adminUpdateBooking,
  getAllQuesModules,
  getBookingQuestions,
  getBookings,
} from '../../../services/apiService';
import BookingQuestionCard from '../_components/BookingQuestionCard';
import DetailsHeader from '../_components/DetailsHeader';
import OverallRatingCard from '../_components/OverallRatingCard';

export default function SessionReview(props) {
  const [booking, setBooking] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editVariableEarning, setEditVariableEarning] = useState(false);
  const [updatedVariableEarning, setUpdatedVariableEarning] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const { ratingTemplateByConceptId } = useSelector(
    (state) => state.questionLevelsReducer
  );
  const history = useHistory();
  const dispatch = useDispatch();

  const handleApproveSession = async () => {
    try {
      setIsApproving(true);
      const payload = {
        id: booking.id,
        stage: BOOKING_STAGES.ADMIN_APPROVED.value,
        variableEarning: updatedVariableEarning || booking.variableEarning,
        totalEarning: updatedVariableEarning
          ? booking.fixedEarning + updatedVariableEarning
          : booking.totalEarning,
      };
      await adminUpdateBooking(payload);
      toast.success('Booking approved');
      history.replace('/manage-bookings');
    } catch (e) {
      console.error(e);
      toast.error('Failed to approve session!');
      setIsApproving(false);
    }
  };

  const handleKeepOnHold = async () => {
    try {
      setIsHolding(true);
      const payload = {
        id: booking.id,
        stage: BOOKING_STAGES.ADMIN_ON_HOLD.value,
      };
      await adminUpdateBooking(payload);
      toast.info('Holded booking!');
      history.replace('/manage-bookings');
    } catch (e) {
      console.error(e);
      toast.error('Failed to approve session!');
      setIsHolding(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const bookingId = props.match.params?.bookingId;
        if (!bookingId) history.replace('/manage-bookings');
        const [bookingResponse, questionsResponse] = await Promise.all([
          getBookings({
            bookingId,
            includeStudent: true,
            includeCoach: true,
            includeSubTopic: true,
          }),
          getBookingQuestions(bookingId),
        ]);
        if (bookingResponse?.items?.length)
          setBooking(bookingResponse.items[0]);
        else throw new Error('Booking not found');
        if (Array.isArray(questionsResponse)) setQuestions(questionsResponse);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch booking details!');
      }
    };
    init();
  }, []);

  useEffect(() => {
    const loadQuestionLevels = async () => {
      try {
        const res = await getAllQuesModules();
        dispatch(setQuestionLevels(res));
      } catch (e) {
        console.error(e);
        toast.error('Failed to fetch details!');
      }
    };
    if (!ratingTemplateByConceptId) loadQuestionLevels();
  }, [ratingTemplateByConceptId]);

  return booking && ratingTemplateByConceptId ? (
    <div>
      <h1 className='text-3xl font-semibold tracking-wide'>Session Approval</h1>
      <DetailsHeader booking={booking} />

      {questions &&
        questions.map((bookingQuestion, index) => (
          <BookingQuestionCard
            bookingQuestion={bookingQuestion}
            ratingTemplate={
              ratingTemplateByConceptId[bookingQuestion.Question.conceptId]
            }
            index={index}
            key={bookingQuestion.Question.id}
          />
        ))}

      {/* Booking Overall Ratings */}
      <OverallRatingCard
        ratingObj={booking.ratingByCoach}
        from='Coach'
        to='Candidate'
      />

      <OverallRatingCard
        ratingObj={booking.ratingByStudent}
        from='Candidate'
        to='Coach'
      />

      <div className='bg-white rounded p-4 mt-8'>
        <div className='flex items-center justify-center gap-8 opacity-60'>
          <div>
            <p>Fixed Earnings</p>
            <p className='font-medium text-2xl'>₹ {booking.fixedEarning}</p>
          </div>
          <div>
            <Add />
          </div>
          <div>
            <p>Variable Earnings</p>
            {editVariableEarning ? (
              <p className='flex gap-2 items-center'>
                <span className='font-medium text-2xl'>₹</span>
                <TextField
                  onChange={(e) =>
                    setUpdatedVariableEarning(Number(e.target.value))
                  }
                  value={
                    updatedVariableEarning === null
                      ? booking.variableEarning
                      : updatedVariableEarning
                  }
                  variant='standard'
                  className='w-20'
                  type='number'
                  autoFocus
                />
                <button
                  onClick={() => setEditVariableEarning(false)}
                  className='text-green-c2'
                >
                  <CheckCircle color='inherit' />
                </button>
                <button
                  className='text-red-c1'
                  onClick={() => {
                    setEditVariableEarning(false);
                    setUpdatedVariableEarning(null);
                  }}
                >
                  <Cancel color='inherit' />
                </button>
              </p>
            ) : (
              <p className='font-medium text-2xl flex items-center gap-2'>
                <span>
                  ₹ {updatedVariableEarning || booking.variableEarning}
                </span>
                <button
                  onClick={() => setEditVariableEarning(true)}
                  className='-mt-1'
                >
                  <Edit />
                </button>
              </p>
            )}
          </div>
          <div>
            <Equal />
          </div>
          <div>
            <p>Total Earnings For This Session</p>
            <p className='font-semibold text-2xl'>
              ₹{' '}
              {updatedVariableEarning
                ? booking.fixedEarning + updatedVariableEarning
                : booking.totalEarning}
            </p>
          </div>
        </div>
        <div className='flex items-center justify-center mt-8'>
          {booking.stage !== BOOKING_STAGES.ADMIN_ON_HOLD.value && (
            <button
              onClick={handleKeepOnHold}
              disabled={isHolding}
              className='w-40 py-2 font-medium text-black text-opacity-60 flex justify-center'
            >
              {isHolding ? (
                <CircularProgress color='inherit' size='1.5rem' />
              ) : (
                <span>Keep on hold</span>
              )}
            </button>
          )}
          <button
            onClick={handleApproveSession}
            disabled={isApproving}
            className='w-48 py-2 bg-blue-c1 font-medium text-white rounded flex justify-center'
          >
            {isApproving ? (
              <CircularProgress color='inherit' size='1.5rem' />
            ) : (
              <span>Approve session</span>
            )}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className='flex items-center justify-center text-blue-c1 mt-20'>
      <CircularProgress color='inherit' />
    </div>
  );
}
